import ErrorComponent from '@components/ErrorComponent/ErrorComponent';
import { strings as errorStrings } from '@components/ErrorComponent/strings';
import extractErrorMessage from '@lib/utils/extractErrorMessage';
import { Spinner, Stack } from '@servicetitan/design-system';
import { useEffect, useState } from 'react';
import strings from './strings';
import CustomerSearchBox, {
  SearchParams,
} from './components/CustomerSearchBox/CustomerSearchBox';
import CustomerSearchResultItem from './components/CustomerSearchResultItem/CustomerSearchResultItem';
import { useCustomerSearchContext } from './context';
import { CustomerBySearchType } from './data/types';
import { FailedSearchInTenantsAlert } from '@components/CustomerSearch/components/FailedSearchInTenantsAlert/FailedSearchInTenantsAlert';

export type SelectCustomerType = Pick<
  CustomerBySearchType,
  'id' | 'name' | 'tenant'
>;
interface CustomerSearchProps {
  tenantId?: string;
  highlightSelectedCustomer?: boolean;
  onSelectCustomer: (customer?: SelectCustomerType) => void;
  isSearchOnLoad?: boolean;
}

const isEmptyParams = (params: SearchParams) =>
  !params.name &&
  !params.phone &&
  !params.street &&
  !params.unit &&
  !params.zip;

const CustomerSearch = ({
  tenantId,
  highlightSelectedCustomer,
  onSelectCustomer,
  isSearchOnLoad = true,
}: CustomerSearchProps) => {
  const {
    searchParams,
    setSearchParams,
    search,
    loading,
    error,
    results,
    failedTenants,
    setFailedTenants,
    setResults,
  } = useCustomerSearchContext();
  const [firstLoad, setFirstLoad] = useState(isSearchOnLoad);
  const [selectedCustomer, setSelectedCustomer] = useState<
    SelectCustomerType | undefined
  >(undefined);

  const [failedTenantsAlert, setFailedTenantsAlert] = useState<boolean>(
    Boolean(failedTenants?.length),
  );

  useEffect(() => {
    if (results === null && firstLoad && tenantId && searchParams.phone) {
      search(searchParams);
    }
  }, [firstLoad, tenantId, searchParams, search, results]);

  useEffect(() => {
    setFailedTenantsAlert(Boolean(failedTenants?.length));
  }, [failedTenants]);

  const handleSearch = (params: SearchParams) => {
    setSearchParams(params);
    search(params);
  };

  const handleSearchParamsChanged = (params: SearchParams) => {
    setSearchParams(params);
    setFailedTenantsAlert(false);
    setFirstLoad(false);
  };

  const handleSearchParamsCleared = () => {
    setResults(null);
    setFailedTenants(null);
    setFailedTenantsAlert(false);
  };

  const handleSelectedCustomer = (customer: SelectCustomerType) => {
    setSelectedCustomer(customer);
    onSelectCustomer({
      id: customer.id,
      name: customer.name,
      tenant: customer.tenant,
    });
  };

  return (
    <Stack direction='column' spacing={3} className='h-100'>
      <CustomerSearchBox
        tenantId={tenantId}
        onSearch={handleSearch}
        onChanged={handleSearchParamsChanged}
        onCleared={handleSearchParamsCleared}
        loading={loading}
      />

      {loading && (
        <Stack justifyContent='center' className='p-t-3' data-testid='loader'>
          <Spinner size='small' />
        </Stack>
      )}

      {failedTenantsAlert && (
        <FailedSearchInTenantsAlert
          failedTenants={failedTenants}
          onClose={() => setFailedTenantsAlert(false)}
        />
      )}

      {error && (
        <Stack justifyContent='center' className='p-t-3'>
          <ErrorComponent
            title={strings.errors.searchError}
            body={`${errorStrings.defaultError} (Error: ${extractErrorMessage(error)})`}
            primaryAction={() => search(searchParams)}
            primaryActionLabel={errorStrings.labels.retry}
          />
        </Stack>
      )}

      {!isEmptyParams(searchParams) &&
        results &&
        !results.length &&
        !loading &&
        !error && (
          <Stack justifyContent='center' className='p-t-3'>
            <ErrorComponent
              image='noResults'
              title={errorStrings.noResultsHeadline}
              body={errorStrings.noResultsMessage}
            />
          </Stack>
        )}

      {results && !loading && !error && (
        <Stack direction='column' spacing={2} className='of-y-auto'>
          {results.map((customer) => {
            return (
              <CustomerSearchResultItem
                key={customer.id}
                customer={customer}
                onClickHandler={() => handleSelectedCustomer(customer)}
                selected={
                  !!highlightSelectedCustomer &&
                  selectedCustomer?.id === customer.id
                }
                // only show tenant name if tenantId is not provided (i.e. multi-tenant search)
                showTenantName={!tenantId}
              />
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};

export default CustomerSearch;
